/* eslint-disable no-param-reassign */
import type { Data } from '../../specProcessor';
import type { StandardChartLayerSpec } from '../../../../core/layer';
import type { Spec as MultiTableLayerSpec } from '../../../../layer/preset/text/multiTable';
import { checkFieldType } from '../helper';
import * as utils from '../../../../util';
import { BaseSpec, ILayerTypeSpec, createLayerProcessor } from '../baseProcessor';

export type Spec = BaseSpec;

type MultiTableLayerType = StandardChartLayerSpec & MultiTableLayerSpec & ILayerTypeSpec;

/**
 * 检查字段类型是否符合要求并记录警告
 * @param values 数据值
 * @param field 字段名
 * @param types 允许的类型数组
 * @param fieldType 字段类型（rows或columns）
 * @param layerType 图层类型
 * @returns 字段类型是否有效
 */
function checkFieldTypeWithWarning(
  values: Record<string, unknown>[],
  field: string,
  types: Array<'string' | 'number' | 'date' | 'array'>,
  fieldType: string,
  layerType: string
): boolean {
  const isTypeValid = types.some(type => checkFieldType(values, field, type));

  if (!isTypeValid) {
    utils.logger.warn(
      'warning',
      `The "${fieldType}" field "${field}" of the "${layerType}" layer should be a ${types.join(
        ' or '
      )} type`
    );
  }

  return isTypeValid;
}

/**
 * 检查树状表格数据类型是否符合要求
 * @param values 数据值
 * @param layerType 图层类型
 * @returns 数据类型是否有效
 */
function validateTreeTableData(values: Record<string, unknown>[], layerType: string): boolean {
  const isValid = Array.isArray(values) && typeof values[0] === 'object';

  if (!isValid) {
    utils.logger.warn(
      'warning',
      `Tree table data for "${layerType}" layer must be a valid array of objects`
    );
  }

  return isValid;
}

// 创建多维表格编码验证器
const validateMultiTableEncoding = (layer: MultiTableLayerType): boolean => {
  const { encoding = {} } = layer;
  const { rows = [], columns = [] } = encoding;

  // 横向一维表格 - 需要rows，不需要columns
  if (rows.length && !columns.length) {
    // 检查columnsNum是否为偶数
    if (layer.columnsNum && layer.columnsNum % 2 !== 0) {
      utils.logger.warn(
        'warning',
        `The "columnsNum" of horizontal table must be an even number, got ${layer.columnsNum}`
      );
      return false;
    }
    return true;
  }

  // 纵向一维表格 - 需要columns，不需要rows
  if (columns.length && !rows.length) {
    return true;
  }

  // 第一列为额外表头的多维表格 - columns > 1 && rows === 1
  if (columns.length > 1 && rows.length === 1) {
    return true;
  }

  // 第一行为额外表头的多维表格 - columns === 1 && rows > 1 && !isTreeTable
  if (columns.length === 1 && rows.length > 1 && !layer.isTreeTable) {
    return true;
  }

  // 树状表格 - 需要isTreeTable为true
  if (layer.isTreeTable) {
    // 树状表格比较特殊，行表头和列表头都是通过数据读取的，而不是提前定义好
    return true;
  }

  // 不符合任何表格类型
  utils.logger.warn(
    'warning',
    'Table configuration does not match any supported table type, please check rows and columns settings'
  );
  return false;
};

// 多维表格数据类型验证 - 由于其复杂性，我们无法使用createDataTypeValidator
const validateMultiTableDataTypes = (layer: MultiTableLayerType, data: Data): boolean => {
  const { encoding } = layer;
  const dataIndex = layer.dataIndex || 0;
  const values = data[dataIndex]?.values || [];

  if (!values.length) {
    return false;
  }

  if (!layer.isTreeTable) {
    // 检查数据类型
    const rows = encoding.rows || [];
    const columns = encoding.columns || [];

    // 检查行字段类型
    const rowsValid = rows.every(rowField =>
      checkFieldTypeWithWarning(values, rowField, ['string', 'date'], 'rows', layer.type)
    );

    // 检查列字段类型
    const columnsValid = columns.every(columnField =>
      checkFieldTypeWithWarning(values, columnField, ['string', 'number'], 'columns', layer.type)
    );

    return rowsValid && columnsValid;
  }
  return validateTreeTableData(values, layer.type);
};

// 额外的检查函数
const additionalMultiTableChecks = (layer: MultiTableLayerType) => {
  // 特殊配置检查
  if (layer.encoding?.rows?.length === 0 && layer.encoding?.columns?.length === 0) {
    utils.logger.warn(
      'warning',
      `At least one of "rows" or "columns" must be specified for "${layer.type}" layer`
    );
    layer.__isEncodingValid = false;
  }
};

// 创建并导出多维表格处理器
export const multiTable = createLayerProcessor<MultiTableLayerType>(
  'multiTable',
  validateMultiTableEncoding,
  validateMultiTableDataTypes,
  additionalMultiTableChecks
);
